import React, { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import api from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const iso = d.toISOString().slice(0, 7);
    opts.push({ value: iso, label: d.toLocaleDateString("en-US", { year: "numeric", month: "long" }) });
  }
  return opts;
}

export default function Reports() {
  const { active } = useCompany();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [pnl, setPnl] = useState(null);
  const [gstr1, setGstr1] = useState(null);
  const [tab, setTab] = useState("pnl");
  const opts = monthOptions();
  const cur = active?.base_currency || "INR";

  useEffect(() => {
    if (!active) return;
    api.get(`/dashboard/pnl?month=${month}`).then((r) => setPnl(r.data)).catch(() => setPnl(null));
    api.get(`/dashboard/gstr1?month=${month}`).then((r) => setGstr1(r.data)).catch(() => setGstr1(null));
  }, [active, month]);

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Reports</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">Financial Reports</h1>
        </div>
        <select data-testid="report-month" className="input-clean w-full sm:w-auto sm:max-w-[240px]" value={month} onChange={(e) => setMonth(e.target.value)}>
          {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button data-testid="tab-pnl" onClick={() => setTab("pnl")}
          className={`px-3 sm:px-4 py-2 text-sm font-medium rounded ${tab === "pnl" ? "bg-[#1E3A8A] text-white" : "bg-white border border-[#E2E8F0]"}`}>Profit & Loss</button>
        <button data-testid="tab-gstr1" onClick={() => setTab("gstr1")}
          className={`px-3 sm:px-4 py-2 text-sm font-medium rounded ${tab === "gstr1" ? "bg-[#1E3A8A] text-white" : "bg-white border border-[#E2E8F0]"}`}>GSTR-1 (India)</button>
      </div>

      {tab === "pnl" && pnl && (
        <div className="space-y-4" data-testid="pnl-report">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="kpi-tile">
              <div className="text-xs text-[#64748B] mb-1">Revenue</div>
              <div className="text-2xl font-bold font-mono-data text-[#16A34A]">{fmtMoney(pnl.revenue, cur)}</div>
            </div>
            <div className="kpi-tile">
              <div className="text-xs text-[#64748B] mb-1">Expenses</div>
              <div className="text-2xl font-bold font-mono-data text-[#DC2626]">{fmtMoney(pnl.total_expenses, cur)}</div>
            </div>
            <div className="kpi-tile">
              <div className="text-xs text-[#64748B] mb-1">Net Profit</div>
              <div className={`text-2xl font-bold font-mono-data ${pnl.net_profit >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
                {fmtMoney(pnl.net_profit, cur)}
              </div>
            </div>
          </div>
          <div className="card-surface p-4">
            <h3 className="font-semibold mb-3">Expenses by Category</h3>
            {Object.keys(pnl.expenses_by_category || {}).length === 0 ? (
              <p className="text-sm text-[#64748B]">No expenses in this period.</p>
            ) : (
              <table className="table-clean">
                <thead><tr><th>Category</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  {Object.entries(pnl.expenses_by_category).map(([cat, amt]) => (
                    <tr key={cat}><td>{cat}</td><td className="text-right font-mono-data">{fmtMoney(amt, cur)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === "gstr1" && gstr1 && (
        <div className="space-y-4" data-testid="gstr1-report">
          {!gstr1.applicable ? (
            <div className="card-surface p-6 text-center">
              <BarChart3 size={40} className="mx-auto text-[#94A3B8] mb-2"/>
              <p className="text-[#64748B]">{gstr1.reason}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="kpi-tile"><div className="text-xs text-[#64748B]">Taxable</div><div className="text-lg font-bold font-mono-data">{fmtMoney(gstr1.totals.taxable, cur)}</div></div>
                <div className="kpi-tile"><div className="text-xs text-[#64748B]">CGST</div><div className="text-lg font-bold font-mono-data">{fmtMoney(gstr1.totals.cgst, cur)}</div></div>
                <div className="kpi-tile"><div className="text-xs text-[#64748B]">SGST</div><div className="text-lg font-bold font-mono-data">{fmtMoney(gstr1.totals.sgst, cur)}</div></div>
                <div className="kpi-tile"><div className="text-xs text-[#64748B]">IGST</div><div className="text-lg font-bold font-mono-data">{fmtMoney(gstr1.totals.igst, cur)}</div></div>
              </div>
              <div className="card-surface p-4">
                <h3 className="font-semibold mb-3">B2B (with GSTIN) — {gstr1.b2b.length} invoices</h3>
                {gstr1.b2b.length === 0 ? <p className="text-sm text-[#64748B]">None.</p> : (
                  <table className="table-clean">
                    <thead><tr><th>Inv#</th><th>Date</th><th>Party</th><th>GSTIN</th><th className="text-right">Taxable</th><th className="text-right">Tax</th><th className="text-right">Total</th></tr></thead>
                    <tbody>{gstr1.b2b.map((r) => (
                      <tr key={r.number}><td className="font-mono-data">{r.number}</td><td>{r.date}</td><td>{r.party}</td><td className="font-mono-data text-xs">{r.gstin}</td>
                        <td className="text-right font-mono-data">{fmtMoney(r.taxable, cur)}</td>
                        <td className="text-right font-mono-data">{fmtMoney((r.cgst || 0) + (r.sgst || 0) + (r.igst || 0), cur)}</td>
                        <td className="text-right font-mono-data font-semibold">{fmtMoney(r.total, cur)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
              <div className="card-surface p-4">
                <h3 className="font-semibold mb-3">B2C (no GSTIN) — {gstr1.b2c.length} invoices</h3>
                {gstr1.b2c.length === 0 ? <p className="text-sm text-[#64748B]">None.</p> : (
                  <table className="table-clean">
                    <thead><tr><th>Inv#</th><th>Date</th><th>Party</th><th className="text-right">Taxable</th><th className="text-right">Tax</th><th className="text-right">Total</th></tr></thead>
                    <tbody>{gstr1.b2c.map((r) => (
                      <tr key={r.number}><td className="font-mono-data">{r.number}</td><td>{r.date}</td><td>{r.party}</td>
                        <td className="text-right font-mono-data">{fmtMoney(r.taxable, cur)}</td>
                        <td className="text-right font-mono-data">{fmtMoney((r.cgst || 0) + (r.sgst || 0) + (r.igst || 0), cur)}</td>
                        <td className="text-right font-mono-data font-semibold">{fmtMoney(r.total, cur)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
